import { useCallback, useEffect, useRef, useState } from 'react';
import { CRM_ORIGIN, notifyParent, type CrmPrefill } from './crm-prefill';
import type { InvoiceKind, SessionUser } from '@qlkho-v3/shared';

import { api, ApiError } from './api';
import { LoadingScreen, Toast } from './components';
import { InvoiceList } from './invoice-list';
import { ArrowDownIcon, ArrowUpIcon, LogoMark, LogoutIcon } from './icons';
import { Login } from './login';
import { LogoutConfirmModal } from './logout-confirm-modal';
import { ServerClock } from './server-clock';

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [kind, setKind] = useState<InvoiceKind>('inbound');
  const [toast, setToast] = useState('');
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [prefill, setPrefill] = useState<CrmPrefill | null>(null);
  const lastPrefill = useRef({ id: 0, at: 0 });

  // BUOC523: nhan don nhap tu CRM (chi chap nhan tin tu trang CRM)
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== CRM_ORIGIN) return;
      const data = event.data as { type?: string; prefill?: CrmPrefill } | null;
      if (!data || data.type !== 'crm-draft-prefill' || !data.prefill || !Array.isArray(data.prefill.items)) return;
      notifyParent({ type: 'crm-prefill-ok', draftId: data.prefill.draftId });
      if (lastPrefill.current.id === data.prefill.draftId && Date.now() - lastPrefill.current.at < 15000) return;
      lastPrefill.current = { id: data.prefill.draftId, at: Date.now() };
      setKind('outbound');
      setPrefill({ ...data.prefill, nonce: Date.now() });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => {
    if (user) notifyParent({ type: 'kho2-ready' });
  }, [user]);
  const clearPrefill = useCallback(() => setPrefill(null), []);

  useEffect(() => {
    api<{ user: SessionUser }>('/api/auth/me')
      .then((result) => setUser(result.user))
      .catch((error) => {
        if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
      })
      .finally(() => setChecking(false));
  }, []);

  const closeToast = useCallback(() => setToast(''), []);

  async function signOut() {
    await api('/api/auth/logout', { method: 'POST' });
    setLogoutOpen(false);
    setUser(null);
  }

  if (checking) return <LoadingScreen />;
  if (!user) return <Login onSuccess={setUser} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="app-brand" href="/" aria-label="KHO VTYT - Trang chính"><LogoMark /><span>KHO VTYT</span><em>V3</em></a>
        <ServerClock />
        <div className="topbar-user">
          <span><strong>{user.fullname}</strong><small>{user.roles[0] ?? 'Nhân viên'}</small></span>
          <button className="icon-button" type="button" onClick={() => setLogoutOpen(true)} aria-label="Đăng xuất" title="Đăng xuất"><LogoutIcon /></button>
        </div>
      </header>
      <div className="app-body">
        <aside className="module-sidebar">
          <div className="module-title">Vận hành</div>
          <nav aria-label="Vận hành kho">
            <button className={kind === 'inbound' ? 'active' : ''} onClick={() => setKind('inbound')}><ArrowDownIcon /><span>Hóa đơn nhập</span></button>
            <button className={kind === 'outbound' ? 'active' : ''} onClick={() => setKind('outbound')}><ArrowUpIcon /><span>Hóa đơn xuất</span></button>
          </nav>
        </aside>
        <main className="workspace">
          <InvoiceList user={user} kind={kind} notify={setToast} prefill={prefill} onPrefillUsed={clearPrefill} />
        </main>
      </div>
      {toast ? <Toast message={toast} onClose={closeToast} /> : null}
      {logoutOpen ? <LogoutConfirmModal onClose={() => setLogoutOpen(false)} onConfirm={signOut} /> : null}
    </div>
  );
}
